import { PlusCircleIcon, TrashIcon } from '@heroicons/react/24/outline';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { ActorRefFrom } from 'xstate';
import type { profileFormMachine } from '../profileForm.machine';
import { ContactCard } from './ContactCard';
import { AddressColgroup } from './contactTableLayout';

const TAGS = CT.profile.contactInfo.address.table;
const CARD_TAGS = CT.profile.contactInfo.address.card;
interface AddressTableProps {
  actor: ActorRefFrom<typeof profileFormMachine>;
  onDelete: (index: number) => void;
}

export const AddressTable = (props: AddressTableProps) => {
  const { actor, onDelete } = props;

  const addresses = useSelector(actor, (state) => state.context.addresses);

  const handleOpen = () => actor.send({ type: 'ON_ADD_ADDRESS' });
  const handleEdit = (index: number) => actor.send({ type: 'ON_EDIT_ADDRESS', index });
  const handleDelete = (index: number) => onDelete(index);
  return (
    <>
      <div className="flex items-center gap-2">
        <div className="flex-auto min-w-0">
          <h2 className="font-semibold text-xl text-content-primary truncate">Addresses</h2>
        </div>
        <div className="flex-none">
          <button
            type="button"
            aria-label="Add address"
            title="Add address"
            data-cy={TAGS.addAddressBtn}
            onClick={handleOpen}
            className="flex items-center justify-center rounded-lg p-1.5 text-primary-50 border border-primary-t-700 hover:bg-primary-t-900 hover:border-primary-t-600 transition-colors cursor-pointer"
          >
            <PlusCircleIcon className="size-5" aria-hidden="true" />
          </button>
        </div>
      </div>
      {/* [MCV-1332] Desktop: the aligned table. Hidden below md, where a phone
          can't fit the columns without horizontal scroll. */}
      <div className="mt-2 hidden md:block flow-root border-2 rounded-md border-gray-2 p-3">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <table className="min-w-full table-fixed divide-y divide-gray-2">
              <AddressColgroup />
              <thead>
                <tr>
                  <th
                    scope="col"
                    className="py-3.5 pr-3 pl-4 text-left text-sm font-semibold text-content-secondary sm:pl-0"
                  >
                    Type
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-semibold text-content-secondary"
                  >
                    Address
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-semibold text-content-secondary"
                  >
                    Address Line 2
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-semibold text-content-secondary"
                  >
                    Postal Code
                  </th>
                  <th scope="col" className="relative py-3.5 pr-4 pl-3 sm:pr-0">
                    <span className="sr-only">Edit</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-2">
                {addresses.map((address, index) => (
                  <tr key={index} data-cy={TAGS.addressRow + index}>
                    <td
                      data-cy={TAGS.addressTypeRow + index}
                      title={address.addressType?.label}
                      className="py-4 pr-3 pl-4 text-sm font-medium truncate text-content-secondary sm:pl-0"
                    >
                      {address.addressType?.label}
                    </td>
                    <td
                      data-cy={TAGS.addressLine1Row + index}
                      title={address.addressLine1}
                      className="px-3 py-4 text-sm truncate text-content-secondary"
                    >
                      {address.addressLine1}
                    </td>
                    <td
                      data-cy={TAGS.addressLine2Row + index}
                      title={address.addressLine2 ?? undefined}
                      className="px-3 py-4 text-sm truncate text-content-secondary"
                    >
                      {address.addressLine2}
                    </td>
                    <td
                      data-cy={TAGS.addressPostalCodeRow + index}
                      title={address.postalCode ?? undefined}
                      className="px-3 py-4 text-sm truncate text-content-secondary"
                    >
                      {address.postalCode}
                    </td>
                    <td className="relative py-4 pr-4 pl-3 text-right text-sm font-medium whitespace-nowrap sm:pr-0">
                      <button
                        type="button"
                        className="text-primary-50 hover:text-primary-200 cursor-pointer"
                        onClick={() => handleEdit(index)}
                      >
                        Edit<span className="sr-only">, {address.addressType?.label}</span>
                      </button>
                      <button
                        type="button"
                        className="text-error hover:text-error-100 cursor-pointer ml-3"
                        onClick={() => handleDelete(index)}
                      >
                        <TrashIcon className="size-4 inline" />
                        <span className="sr-only">Delete {address.addressType?.label}</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {/* [MCV-1332] Mobile: stacked cards instead of a horizontally-scrolling table. */}
      <ul className="md:hidden mt-2 flex flex-col gap-3">
        {addresses.map((address, index) => (
          <ContactCard
            key={address.addressType?.value ?? index}
            dataCy={CARD_TAGS.row + index}
            title={address.addressType?.label}
            fields={[
              { label: 'Address', value: address.addressLine1, title: address.addressLine1 },
              ...(address.addressLine2
                ? [
                    {
                      label: 'Address Line 2',
                      value: address.addressLine2,
                      title: address.addressLine2,
                    },
                  ]
                : []),
              {
                label: 'Postal Code',
                value: address.postalCode,
                title: address.postalCode ?? undefined,
              },
            ]}
            onEdit={() => handleEdit(index)}
            onDelete={() => handleDelete(index)}
            label={address.addressType?.label}
          />
        ))}
      </ul>
    </>
  );
};
